// oxlint-disable max-lines-per-function
import { mergeWith } from 'lodash-es';
import { assertEvent, enqueueActions } from 'xstate';

import { machine } from './recommendationsActor';

const merge = <OBJ, SRC1, SRC2>(object: OBJ, source1: SRC1, source2: SRC2): OBJ & SRC1 & SRC2 => {
	return mergeWith(object, source1, source2, (_objValue, srcValue, key) =>
		key === 'recommendations' && Array.isArray(srcValue) ? srcValue : undefined,
	);
};

export const createLogic = () =>
	machine.provide({
		actions: {
			onSubmit: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'submit');

				const updatedDraft = merge({}, context.draft, event.draft);

				enqueue.assign({ draft: updatedDraft });
				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),

			onError: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'error');

				const currentDraft = context.draft;

				const { issuePrompt = '', personalStory = '' } = currentDraft?.input ?? {};
				const description = `${issuePrompt} <br /><br />${personalStory}`;

				const userDraft = {
					type: 'user_draft',
					petitionDraft: { ask: null, description, image: null },
					recommendedDraft: null,
					metadata: { imageJobId: null },
				};

				const updatedDraft = merge({}, context.draft, userDraft);

				enqueue.assign({ draft: updatedDraft });

				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),

			unsetDraftType: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'back');

				const targetBranch = event.target;

				const updatedDraft = (() => {
					if (targetBranch === null) return merge({}, context.draft, { type: null });
					return context.draft;
				})();

				enqueue.assign({ draft: updatedDraft });

				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),

			unsetRecommendationsDraftType: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'back');

				const updatedDraft = merge({}, context.draft, { type: 'recommendations_variant_b_draft' });

				enqueue.assign({ draft: updatedDraft });

				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),

			persistDraftType: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'submit');

				const baseDraft = merge({}, context.draft, event.draft);

				const targetBranch = event.target;

				const updatedDraft = (() => {
					if (targetBranch === null) return merge({}, baseDraft, { type: null });
					if (targetBranch === 'ai') return merge({}, baseDraft, { type: 'recommendations_variant_b_draft' });
					if (targetBranch === 'user') return merge({}, baseDraft, { type: 'user_draft' });
					return baseDraft;
				})();

				enqueue.assign({ draft: updatedDraft });

				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),

			persistRecommendationsDraftType: enqueueActions(({ context, event, enqueue }) => {
				assertEvent(event, 'submit');

				const baseDraft = merge({}, context.draft, event.draft);

				const targetBranch = event.target;

				const updatedDraft = (() => {
					if (targetBranch === 'ai') return merge({}, baseDraft, { type: 'recommendations_variant_b_draft_ai' });
					if (targetBranch === 'user') return merge({}, baseDraft, { type: 'recommendations_variant_b_draft_user' });
					return baseDraft;
				})();

				enqueue.assign({ draft: updatedDraft });

				enqueue.sendParent({ type: 'sync_draft', draft: updatedDraft });
			}),
		},

		guards: {
			transitionTo: ({ event }, params) => {
				assertEvent(event, 'submit');
				return event.target === params.target;
			},

			isUserDraft: ({ context, event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'user';
				}
				return context.draft?.type === 'user_draft';
			},

			isAiDraft: ({ context, event }) => {
				if (event.type === 'submit' && event.target) {
					return event.target === 'ai';
				}
				return context.draft?.type === 'ai_draft';
			},

			isRecommendationsVariantBDraft: ({ context }) => {
				if (context.draft?.metadata?.variant !== 'recommendations_variant_b') {
					return false;
				}
				return context.draft.type === 'recommendations_variant_b_draft';
			},

			isRecommendationsVariantBAiDraft: ({ context, event }) => {
				if (context.draft?.metadata?.variant !== 'recommendations_variant_b') {
					return false;
				}
				if (event.type === 'submit' && event.target) {
					return event.target === 'ai';
				}
				return context.draft.type === 'recommendations_variant_b_draft_ai';
			},

			isRecommendationsVariantBUserDraft: ({ context, event }) => {
				if (context.draft?.metadata?.variant !== 'recommendations_variant_b') {
					return false;
				}
				if (event.type === 'submit' && event.target) {
					return event.target === 'user';
				}
				return context.draft.type === 'recommendations_variant_b_draft_user';
			},
		},
	});
